/**
 * Shared authentication types for API contracts between frontend and backend
 */
import { z } from "zod";
// ============================================
// Input Schemas
// ============================================
export const RegisterInputSchema = z.object({
    email: z.string().email(),
    password: z.string(),
    firstName: z.string(),
    lastName: z.string(),
    phone: z.string().optional(),
    isOnboardingCompany: z.boolean().optional(),
});
export const LoginInputSchema = z.object({
    email: z.string().email(),
    password: z.string(),
});
export const RefreshTokenInputSchema = z.object({
    refreshToken: z.string().optional(),
});
export const LogoutInputSchema = z.object({
    refreshToken: z.string().optional(),
});
// ============================================
// Response Schemas
// ============================================
export const TokensResponseSchema = z.object({
    accessToken: z.string(),
    refreshToken: z.string(),
});
export const EmploymentInfoSchema = z.object({
    id: z.string().uuid(),
    companyId: z.string().uuid(),
    companyName: z.string(),
    role: z.enum(["OWNER", "MANAGER", "EMPLOYEE", "ADMIN"]),
    isDesktopUser: z.boolean(),
});
export const AuthUserResponseSchema = z.object({
    id: z.string().uuid(),
    email: z.string().email(),
    firstName: z.string(),
    lastName: z.string(),
    photoUrl: z.string().nullable(),
    companyId: z.string().uuid().nullable(),
    isGodMode: z.boolean().optional(),
    tabPermissions: z.array(z.string()).optional(),
    featurePermissions: z.array(z.string()).optional(),
});
export const AuthResponseSchema = z.object({
    user: AuthUserResponseSchema,
    employments: z.array(EmploymentInfoSchema),
    tokens: TokensResponseSchema,
});
export const CheckStatusDataSchema = z.object({
    userExists: z.boolean(),
    needsOnboarding: z.boolean(),
    userType: z.enum(["owner"]).nullable(),
    companyId: z.string().uuid().nullable(),
    companyName: z.string().nullable(),
    isDesktopUser: z.boolean().optional(),
    user: AuthUserResponseSchema.optional(),
});
export const CheckStatusResponseSchema = z.object({
    success: z.boolean(),
    data: CheckStatusDataSchema,
});
export const AuthMessageResponseSchema = z.object({
    message: z.string(),
});
export const AccessTokenResponseSchema = z.object({
    accessToken: z.string(),
    refreshToken: z.string(),
});
// ============================================
// Password Reset Schemas
// ============================================
export const RequestPasswordResetInputSchema = z.object({
    email: z.string().email(),
    isMobileUser: z.boolean().optional(),
});
export const RequestPasswordResetResponseSchema = z.object({
    message: z.string(),
    otp: z.string().optional(),
});
export const VerifyResetOtpInputSchema = z.object({
    otp: z.string().length(6),
});
export const ResetPasswordInputSchema = z.object({
    completionToken: z.string(),
    password: z
        .string()
        .min(8)
        .regex(/[A-Z]/, "Must contain an uppercase letter")
        .regex(/[a-z]/, "Must contain a lowercase letter")
        .regex(/[0-9]/, "Must contain a number"),
});
export const VerifyResetOtpResponseSchema = z.object({
    completionToken: z.string(),
});
// ============================================
// Alias Exports for Contract Compatibility
// ============================================
// These allow the contract to import with its expected names
export const UserResponseSchema = AuthUserResponseSchema;
export const LogoutResponseSchema = AuthMessageResponseSchema;
export const RefreshResponseSchema = AccessTokenResponseSchema;
